import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ScrollText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { isCanceled, useDebounced, useLatestRequest } from "../../../lib/useLatest";
import { PageHeader } from "../../../components/PageHeader";
import { LoadMore } from "../../../components/LoadMore";
import { MonError } from "./MonToolbar";
import { LogsTable } from "./LogsTable";
import { LogFilters, EMPTY_FILTERS } from "./LogFilters";
import { LogDetail } from "./LogDetail";
import { KINDS, monErr, wibInputToIso } from "./monUtil";

const toParams = (f) => {
  const p = { limit: 50 };
  ["kind", "name", "instance"].forEach((k) => { if (f[k]) p[k] = f[k]; });
  if (f.q.trim()) p.q = f.q.trim();
  if (f.status) p.status = Number(f.status);
  if (f.min_ms) p.min_ms = Number(f.min_ms);
  if (f.errors_only) p.errors_only = true;
  if (f.from) p.from = wibInputToIso(f.from);
  if (f.to) p.to = wibInputToIso(f.to);
  return p;
};

export default function MonitoringLogs() {
  const [sp] = useSearchParams();
  const [f, setF] = useState(() => ({ ...EMPTY_FILTERS, kind: KINDS[sp.get("kind")] ? sp.get("kind") : "", name: sp.get("name") || "" }));
  const [items, setItems] = useState(null); const [cursor, setCursor] = useState(null); const [more, setMore] = useState(false);
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false); const [sel, setSel] = useState(null);
  const next = useLatestRequest();
  const dq = useDebounced(f.q); const dStatus = useDebounced(f.status); const dMin = useDebounced(f.min_ms); const dInst = useDebounced(f.instance);
  const params = useMemo(() => toParams({ ...f, q: dq, status: dStatus, min_ms: dMin, instance: dInst }), [f.kind, f.name, f.errors_only, f.from, f.to, dq, dStatus, dMin, dInst]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback(async (before) => {
    setLoading(true);
    try {
      const r = await api.get("/platform/monitoring/logs", { params: { ...params, ...(before ? { before } : {}) }, signal: next() });
      setItems((prev) => (before ? [...(prev || []), ...(r.data.items || [])] : r.data.items || []));
      setCursor(r.data.next_before || null); setMore(!!r.data.has_more); setErr("");
    } catch (e) { if (isCanceled(e)) return; if (before) toast.error(monErr(e)); else { setErr(monErr(e)); setItems([]); } } finally { setLoading(false); }
  }, [params, next]);
  useEffect(() => { setItems(null); load(); }, [load]);
  const instances = useMemo(() => [...new Set((items || []).map((l) => l.instance).filter(Boolean))], [items]);
  return (
    <div data-testid="platform-monitoring-logs">
      <PageHeader icon={ScrollText} title="Log Latensi" description="Log mentah per operasi (retensi 7 hari, terbaru dulu). DB hanya dicatat bila ≥ 100 ms atau gagal. Error = 5xx/exception.">
        <button onClick={() => load()} disabled={loading} className="btn-soft rounded-xl px-3 py-2 text-xs" data-testid="mon-logs-refresh"><RefreshCw size={13} className={loading && !cursor ? "animate-spin" : ""} /> Segarkan</button>
      </PageHeader>
      <MonError msg={err} />
      <LogFilters f={f} set={(patch) => setF((x) => ({ ...x, ...patch }))} instances={instances} />
      <LogsTable items={items} onOpen={setSel} />
      {items && more && <LoadMore onClick={() => load(cursor)} loading={loading} testid="mon-logs-load-more" />}
      {items && items.length > 0 && !more && <p className="py-3 text-center text-[11px] text-slate-400" data-testid="mon-logs-end">Semua log sudah dimuat ({items.length}).</p>}
      <LogDetail id={sel} onClose={() => setSel(null)} />
    </div>
  );
}
